import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const MIN_PASSWORD_LENGTH = 8;

// Both halves of the reset flow, chosen by whether the URL carries a token:
// no token means "email me a link", a token means "set a new password".
// One route keeps the emailed link pointing somewhere that always works, even
// if it's opened twice or long after it expired.
function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const navigate = useNavigate();
  const { requestPasswordReset, resetPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitRequest = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      console.log('Password reset request error:', err);
      setError('Could not send the reset email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Those passwords do not match.');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword(token, password);
      navigate('/');
    } catch (err) {
      console.log('Password reset error:', err);
      setError('That reset link is no longer valid. Please request a new one.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 rounded-lg bg-white/5 border border-nlg-rule text-nlg-text placeholder:text-nlg-muted focus:outline-none focus:border-nlg-gold/50';
  const buttonClass = (enabled) => `w-full px-4 py-2 rounded-full font-bold transition-transform duration-150 flex items-center justify-center ${
    enabled ? 'bg-nlg-gold text-nlg-bg hover:scale-[1.02]' : 'bg-white/5 border border-nlg-rule text-nlg-muted cursor-not-allowed'
  }`;

  const requestValid = email.trim().length > 0;
  const resetValid = password.length > 0 && confirmPassword.length > 0;

  return (
    <div
      onClick={() => navigate('/')}
      className="flex-grow w-full flex items-center justify-center p-3 relative z-10"
    >
      <div className="max-w-md w-full">
        <section
          onClick={(e) => e.stopPropagation()}
          className="w-full bg-nlg-block border border-nlg-rule rounded-2xl p-5 sm:p-7"
        >
          {!token ? (
            <>
              <div className="text-center mb-6">
                <h3 className="font-display font-black text-nlg-cream text-xl sm:text-2xl mb-1">
                  Reset your password
                </h3>
                <p className="text-sm text-nlg-muted">
                  We'll email you a link to set a new one.
                </p>
              </div>

              {sent ? (
                // Deliberately the same message whether or not the address
                // matched an account - the server won't tell us, on purpose.
                <div className="space-y-4">
                  <p className="text-sm text-nlg-text text-center">
                    If that address has an account, a reset link is on its way. It's good for an hour.
                  </p>
                  <div className="text-center">
                    <Link to="/login" className="text-sm text-nlg-gold font-semibold">
                      Back to sign in
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={submitRequest} className="space-y-4">
                  <div>
                    <label htmlFor="reset-email" className="block text-nlg-text font-medium mb-1">Email</label>
                    <input
                      id="reset-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputClass}
                    />
                  </div>

                  {error && (
                    <div className="bg-nlg-coral/10 border border-nlg-coral/30 text-nlg-coral px-3 py-2 rounded-lg text-sm text-center" role="alert">
                      {error}
                    </div>
                  )}

                  <button type="submit" disabled={isSubmitting || !requestValid} className={buttonClass(requestValid && !isSubmitting)}>
                    {isSubmitting ? 'Sending…' : 'Send reset link'}
                  </button>

                  <div className="text-center">
                    <Link to="/login" className="text-sm text-nlg-gold font-semibold">
                      Back to sign in
                    </Link>
                  </div>
                </form>
              )}
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <h3 className="font-display font-black text-nlg-cream text-xl sm:text-2xl mb-1">
                  Choose a new password
                </h3>
              </div>

              <form onSubmit={submitReset} className="space-y-4">
                <div>
                  <label htmlFor="reset-password" className="block text-nlg-text font-medium mb-1">New password</label>
                  <input
                    id="reset-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass}
                  />
                  <p className="mt-1 text-sm text-nlg-muted">At least {MIN_PASSWORD_LENGTH} characters.</p>
                </div>

                <div>
                  <label htmlFor="reset-confirm" className="block text-nlg-text font-medium mb-1">Confirm password</label>
                  <input
                    id="reset-confirm"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={inputClass}
                  />
                </div>

                {error && (
                  <div className="bg-nlg-coral/10 border border-nlg-coral/30 text-nlg-coral px-3 py-2 rounded-lg text-sm text-center" role="alert">
                    {error}
                  </div>
                )}

                <button type="submit" disabled={isSubmitting || !resetValid} className={buttonClass(resetValid && !isSubmitting)}>
                  {isSubmitting ? 'Saving…' : 'Set new password'}
                </button>

                <div className="text-center">
                  <Link to="/reset-password" className="text-sm text-nlg-gold font-semibold">
                    Request a new link
                  </Link>
                </div>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default ResetPassword;
